import React from 'react'
import { useState, useEffect } from 'react'
import axios from 'axios'
import Swal from 'sweetalert2'
import {
  fetchCustomizedData,
  createCustomizedData,
  deleteCustomizedQuestion,
  updateBookDemoSettings,
  updateConnectExpertSettings,
} from '../../../../utils/customizeBot'
import './customizeBot.scss'
import { fetchColors, addColor } from '../../../../utils/widgetColor'
import { fetchBotSettings, botSettings } from '../../../../utils/botSettings'
import { fetchGreetingMessage, createOrUpdateGreetingMessage } from "../../../../utils/greetingMessage"
import { fetchWelcomeMessage, createOrUpdateWelcomeMessage } from "../../../../utils/welcomeMessage"

const API_URL = process.env.REACT_APP_API_URL

interface Question {
  question: string
  label: string
}

export const CustomizeBot = () => {
  const [questions, setQuestions] = useState<Question[]>([])
  const [newQuestion, setNewQuestion] = useState<string>('')
  const [newLabel, setNewLabel] = useState<string>('')
  const [headerColor, setHeaderColor] = useState<string>('')
  const [chatIconColor, setChatIconColor] = useState<string>('')
  const [userInputBackgroundColor, setUserInputBackgroundColor] = useState<string>('')
  const [botResponseBackgroundColor, setBotResponseBackgroundColor] = useState<string>('')
  const [colors, setColors] = useState<string[]>([])
  const [botImageFile, setBotImageFile] = useState<File | null>(null)
  const [botImagePreview, setBotImagePreview] = useState<string>('') // URL or path of existing bot image
  const [botEmail, setBotEmail] = useState<string>('')
  const [welcomeMessage, setWelcomeMessage] = useState<string>('')
  const [greetingMessage, setGreetingMessage] = useState<string>('')
  const [enableBookDemo, setEnableBookDemo] = useState<boolean>(false)
  const [calendlyEventTypeUri, setCalendlyEventTypeUri] = useState<string>('')
  const [calendlySchedulingUrl, setCalendlySchedulingUrl] = useState<string>('')
  const [enableConnectExpert, setEnableConnectExpert] = useState<boolean>(false)
  const [expertEmail, setExpertEmail] = useState<string>('')
  const [expertPhone, setExpertPhone] = useState<string>('')


  const getBotIDFromURL = () => {
    const urlParts = window.location.href.split('/')
    const botID = urlParts[urlParts.length - 1] // Get the last part of the URl
    return botID
  }

  const handleHeaderColorChange = (e) => {
    setHeaderColor(e.target.value)
  }

  const handleChatIconColorChange = (e) => {
    setChatIconColor(e.target.value)
  }
  const handleUserInputBackgroundColor = (e) => {
    setUserInputBackgroundColor(e.target.value)
  }

  const handleBotResponseBackgroundColor = (e) => {
    setBotResponseBackgroundColor(e.target.value)
  }

  const handleWelcomeMessageChange = (e) => {
    setWelcomeMessage(e.target.value)
  }

  const handleGreetingMessageChange = (e) => {
    setGreetingMessage(e.target.value)
  }


  const handleBotImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setBotImageFile(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setBotImagePreview(reader.result as string); // Base64 string
      };

      reader.readAsDataURL(file);
    }
  };

  const handleBotEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBotEmail(e.target.value)
  }

  const botID = getBotIDFromURL()

  useEffect(() => {
    // Fetch existing questions and labels when the component mounts
    fetchCustomizedData(botID)
      .then((data) => {
        if (data && data[0]?.sampleQuestions?.length > 0) {
          setQuestions(data[0].sampleQuestions);
        } else {
          console.log('No sample question');
        }

        if (data && data[0]) {
          setEnableBookDemo(Boolean(data[0].enableBookDemo));
          setCalendlyEventTypeUri(data[0].calendlyEventTypeUri || '');
          setCalendlySchedulingUrl(data[0].calendlySchedulingUrl || '');
          setEnableConnectExpert(Boolean(data[0].enableConnectExpert));
          setExpertEmail(data[0].expertEmail || '');
          setExpertPhone(data[0].expertPhone || '');
        }
      })
      .catch((error) => console.error('Error setting questions:', error));

    // Fetch existing colors when the component mounts
    fetchColors(botID)
      .then((data) => {
        console.log(data)
        setColors(data)
        // Set the input fields with the fetched color data
        if (data.length > 0) {
          setHeaderColor(data[0].headerColor)
          setChatIconColor(data[0].chatIconColor)
          setUserInputBackgroundColor(data[0].userInputBackgroundColor)
          setBotResponseBackgroundColor(data[0].botResponseBackgroundColor)
        }
      })
      .catch((error) => console.error('Error setting colors:', error))

    fetchBotSettings(botID)
      .then(({ botEmail, botImageUrl }) => {
        if (botEmail) setBotEmail(botEmail);

        // Detect if it's a valid Base64 string or a URL
        if (botImageUrl) {
          // Ensure the image URL has a proper data URL prefix
          const hasDataPrefix = botImageUrl.startsWith("data:image/");
          const imagePreview = hasDataPrefix
            ? botImageUrl
            : `data:image/png;base64,${botImageUrl}`;

          setBotImagePreview(imagePreview);
        }
      })
      .catch((error) => console.error('Error fetching bot settings:', error));

    // Fetch welcome message
    fetchWelcomeMessage(botID)
      .then((message) => {
        if (message) {
          console.log("Fetched welcome message:", message);
          setWelcomeMessage(message);
        } else {
          console.log('No welcome message found');
        }
      })
      .catch((error) => console.error('Error fetching welcome message:', error));


    // fetch greeting message
    fetchGreetingMessage(botID)
      .then((message) => {
        if (message) {
          console.log("Fetched greeting message:", message);
          setGreetingMessage(message);
        } else {
          console.log('No greeting message found');
        }
      })
      .catch((error) => console.error('Error fetching greeting message:', error));


  }, [])


  const addQuestion = async () => {
    if (newQuestion && newLabel) {
      try {
        // Create or update a bot tab with the new question and label
        await createCustomizedData(botID, [...questions, { question: newQuestion, label: newLabel }])
        // Fetch updated bot tabs after adding a new question
        fetchCustomizedData(botID)
          .then((data) => setQuestions(data[0].sampleQuestions))
          .catch((error) => console.error('Error setting questions after adding:', error))
        setNewQuestion('')
        setNewLabel('')
      } catch (error) {
        console.error('Error adding question:', error)
      }
    }
  }

  const handleDeleteQuestion = async (index) => {
    // Show SweetAlert2 confirmation dialog
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.isConfirmed) {
        // If user confirms, proceed with the deletion
        deleteQuestion(index)
      }
    })
  }

  const deleteQuestion = async (index: number) => {
    try {
      // Delete the question at the specified index
      await deleteCustomizedQuestion(botID, index)
      // Fetch updated bot tabs after deleting a question
      fetchCustomizedData(botID)
        .then((data) => setQuestions(data[0].sampleQuestions))
        .catch((error) => console.error('Error setting questions after deleting:', error))
    } catch (error) {
      console.error('Error deleting question:', error)
    }
  }

  const handleApplyColors = async () => {
    try {
      // Update or add colors for the bot
      await addColor(botID, { headerColor, chatIconColor, userInputBackgroundColor, botResponseBackgroundColor })
      // Fetch updated colors after applying
      fetchColors(botID)
        .then((data) => {
          console.log(data)
          setColors(data)
          // Set the input fields with the fetched color data
          if (data.length > 0) {
            setHeaderColor(data[0].headerColor)
            setChatIconColor(data[0].chatIconColor)
            setUserInputBackgroundColor(data[0].userInputBackgroundColor)
            setBotResponseBackgroundColor(data[0].botResponseBackgroundcolor)
          }
        })
        .catch((error) => console.error('Error setting colors after applying:', error))
    } catch (error) {
      console.error('Error applying colors:', error)
    }
  }

  const handleApplySettings = async () => {
    console.log("hello111111");

    if (!botImageFile && !botEmail) {
      Swal.fire('No Changes', 'Please upload an image or update the email address.', 'info')
      return
    }

    try {
      await botSettings(botID, { botImageFile, botEmail })
    } catch (error) {
      console.error('Failed to apply settings:', error)
    }
  }

  const handleApplyWelcomeMessage = async () => {
    try {
      await createOrUpdateWelcomeMessage(botID, welcomeMessage)
      Swal.fire('Success', 'Welcome message saved!', 'success')
    } catch (error) {
      console.error('Error saving welcome message:', error)
      Swal.fire('Error', 'Failed to save welcome message', 'error')
    }
  }

  const handleApplyBookDemoSettings = async () => {
    try {
      await updateBookDemoSettings(botID, enableBookDemo, calendlyEventTypeUri, calendlySchedulingUrl)
    } catch (error) {
      console.error('Error saving book-demo settings:', error)
    }
  }

  const handleApplyConnectExpertSettings = async () => {
    try {
      await updateConnectExpertSettings(botID, enableConnectExpert, expertEmail, expertPhone)
    } catch (error) {
      console.error('Error saving expert settings:', error)
    }
  }

  const handleApplyGreetingMessage = async () => {
    try {
      await createOrUpdateGreetingMessage(botID, greetingMessage)
      Swal.fire('Success', 'Greeting message saved!', 'success')
    } catch (error) {
      console.error('Error saving greeting message:', error)
      Swal.fire('Error', 'Failed to save greeting message', 'error')
    }
  }


  return (
    <div className='p-5 d-flex flex-column'>
      <div className='d-flex flex-row w-75 justify-content-between mb-5 h-50'>
        <div>
          <h1>Add Sample Questions</h1>
          <form>
            <div className='row mt-5 mb-6'>
              <label className='required fw-bold fs-6'>Sample Question:</label>
              <div className='fv-row mt-2'>
                <input
                  type='text'
                  className='form-control form-control-solid'
                  placeholder='Enter sample question'
                  value={newQuestion}
                  onChange={(e) => setNewQuestion(e.target.value)}
                />
              </div>
            </div>
            <div className='row mt-5 mb-6'>
              <label className='required fw-bold fs-6'>Label:</label>
              <div className='fv-row mt-2'>
                <input
                  type='text'
                  className='form-control form-control-solid'
                  placeholder='Enter label for question'
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                />
              </div>
            </div>
            <button className='btn btn-success mt-2' type='button' onClick={addQuestion}>
              Add Question
            </button>
          </form>
        </div>

        <div className='table-container  w-35'>
          <h2 className='mb-2'>Existing Suggestions</h2>
          {questions.length < 1 ? (
            <div>No Suggestion questions found</div>
          ) : (
            <>
              <table className='table'>
                <thead>
                  <tr>
                    <th scope='col'>#</th>
                    <th scope='col'>Question</th>
                    <th scope='col'>Label</th>
                    <th scope='col'>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {questions.map((q, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                      <th scope='row'>{index + 1}</th>
                      <td>{q.question}</td>
                      <td>{q.label}</td>
                      <td>
                        <button
                          className='removeDomain'
                          onClick={() => handleDeleteQuestion(index)}
                        >
                          <i className='bi bi-trash'></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
      <div className='d-flex mb-5'>
        <div className='w-35'>
          <h2>Customize Welcome Message</h2>
          <form>
            <div className='row mt-5 mb-6'>
              <label className='fw-bold fs-6'>Add Message:</label>
              <div className='fv-row mt-2'>
                <input
                  type='text'
                  className='form-control form-control-solid colored-border'
                  placeholder='Enter Your Message'
                  value={welcomeMessage}
                  onChange={handleWelcomeMessageChange}
                />
              </div>
            </div>
            <button className='btn btn-success mt-2' type='button' onClick={handleApplyWelcomeMessage}>
              Add Message
            </button>
          </form>
        </div>
      </div>
      <div className='d-flex mb-5'>
        <div className='w-35'>
          <h2>Customize Greeting Message</h2>
          <form>
            <div className='row mt-5 mb-6'>
              <label className='fw-bold fs-6'>Add Greeting:</label>
              <div className='fv-row mt-2'>
                <input
                  type='text'
                  className='form-control form-control-solid colored-border'
                  placeholder='Enter Greeting Message'
                  value={greetingMessage}
                  onChange={handleGreetingMessageChange}
                />
              </div>
            </div>
            <button className='btn btn-success mt-2' type='button' onClick={handleApplyGreetingMessage}>
              Add Greeting
            </button>
          </form>
        </div>
      </div>

      <div className='d-flex mb-5'>
        <div className='w-35'>
          <h2>Book a Demo (Calendly)</h2>
          <form>
            <div className='row mt-5 mb-6'>
              <div className='form-check form-switch fv-row'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id='enableBookDemo'
                  checked={enableBookDemo}
                  onChange={(e) => setEnableBookDemo(e.target.checked)}
                />
                <label className='form-check-label fw-bold fs-6' htmlFor='enableBookDemo'>
                  Enable "Book a Demo" for this bot
                </label>
              </div>
            </div>
            <div className='row mt-5 mb-6'>
              <label className='fw-bold fs-6'>Calendly Event Type URI:</label>
              <div className='fv-row mt-2'>
                <input
                  type='text'
                  className='form-control form-control-solid colored-border'
                  placeholder='https://api.calendly.com/event_types/xxxxxxxx'
                  value={calendlyEventTypeUri}
                  onChange={(e) => setCalendlyEventTypeUri(e.target.value)}
                />
              </div>
            </div>
            <div className='row mt-5 mb-6'>
              <label className='fw-bold fs-6'>Calendly Scheduling URL:</label>
              <div className='fv-row mt-2'>
                <input
                  type='text'
                  className='form-control form-control-solid colored-border'
                  placeholder='https://calendly.com/your-org/demo'
                  value={calendlySchedulingUrl}
                  onChange={(e) => setCalendlySchedulingUrl(e.target.value)}
                />
              </div>
            </div>
            <button className='btn btn-success mt-2' type='button' onClick={handleApplyBookDemoSettings}>
              Save Book a Demo Settings
            </button>
          </form>
        </div>
      </div>

      <div className='d-flex mb-5'>
        <div className='w-35'>
          <h2>Connect with Expert</h2>
          <form>
            <div className='row mt-5 mb-6'>
              <div className='form-check form-switch fv-row'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id='enableConnectExpert'
                  checked={enableConnectExpert}
                  onChange={(e) => setEnableConnectExpert(e.target.checked)}
                />
                <label className='form-check-label fw-bold fs-6' htmlFor='enableConnectExpert'>
                  Enable "Connect with Expert" for this bot
                </label>
              </div>
            </div>
            <div className='row mt-5 mb-6'>
              <label className='fw-bold fs-6'>Expert Email:</label>
              <div className='fv-row mt-2'>
                <input
                  type='email'
                  className='form-control form-control-solid colored-border'
                  placeholder='support@example.com'
                  value={expertEmail}
                  onChange={(e) => setExpertEmail(e.target.value)}
                />
              </div>
            </div>
            <div className='row mt-5 mb-6'>
              <label className='fw-bold fs-6'>Expert Phone Number:</label>
              <div className='fv-row mt-2'>
                <input
                  type='tel'
                  className='form-control form-control-solid colored-border'
                  placeholder='+91 98765 43210'
                  value={expertPhone}
                  onChange={(e) => setExpertPhone(e.target.value)}
                />
              </div>
            </div>
            <button className='btn btn-success mt-2' type='button' onClick={handleApplyConnectExpertSettings}>
              Save Connect with Expert Settings
            </button>
          </form>
        </div>
      </div>

      <div className='d-flex mb-5'>
        <div className='w-35'>
          <h2>Customize Header and Chat Icon</h2>
          <form>
            <div className='row mt-5 mb-6'>
              <label className='fw-bold fs-6'>Header Color:</label>
              <div className='fv-row mt-2'>
                <input
                  type='text'
                  className='form-control form-control-solid colored-border'
                  placeholder='Enter header color'
                  value={headerColor}
                  onChange={handleHeaderColorChange}
                  style={{ borderColor: headerColor }}
                />
              </div>
            </div>
            <div className='row mt-5 mb-6'>
              <label className='fw-bold fs-6'>Chat Icon Color:</label>
              <div className='fv-row mt-2'>
                <input
                  type='text'
                  className='form-control form-control-solid colored-border'
                  placeholder='Enter chat icon color Eg. #007bff'
                  value={chatIconColor}
                  onChange={handleChatIconColorChange}
                  style={{ borderColor: chatIconColor }}
                />
              </div>
            </div>
            <div className='row mt-5 mb-6'>
              <label className='fw-bold fs-6'>User Input Background Color:</label>
              <div className='fv-row mt-2'>
                <input
                  type='text'
                  className='form-control form-control-solid colored-border'
                  placeholder='Enter chat icon color Eg. #007bff'
                  value={userInputBackgroundColor}
                  onChange={handleUserInputBackgroundColor}
                  style={{ borderColor: userInputBackgroundColor }}
                />
              </div>
            </div>
            <div className='row mt-5 mb-6'>
              <label className='fw-bold fs-6'>Bot Response Background Color:</label>
              <div className='fv-row mt-2'>
                <input
                  type='text'
                  className='form-control form-control-solid colored-border'
                  placeholder='Enter chat icon color Eg. #007bff'
                  value={botResponseBackgroundColor}
                  onChange={handleBotResponseBackgroundColor}
                  style={{ borderColor: botResponseBackgroundColor }}
                />
              </div>
            </div>
            <button className='btn btn-success mt-2' type='button' onClick={handleApplyColors}>
              Apply Colors
            </button>
          </form>
        </div>
      </div>
      <div className='d-flex'>
        <div className='w-35'>
          <h2>Customize Website Bot Image and their Email</h2>
          <form>
            {/* Bot Image Upload */}
            <div className='row mt-5 mb-6'>
              <label className='fw-bold fs-6'>Upload Bot Image:</label>
              <div className='fv-row mt-2'>
                <input
                  type='file'
                  className='form-control form-control-solid colored-border'
                  accept='image/*'
                  onChange={handleBotImageUpload}
                />
                {botImagePreview && (
                  <div className='mt-3'>
                    <img
                      src={botImagePreview}
                      alt='Bot'
                      style={{ width: '80px', height: '80px', borderRadius: '5px', objectFit: 'cover' }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Website Email Address */}
            <div className='row mt-5 mb-6'>
              <label className='fw-bold fs-6'>Website Email Address:</label>
              <div className='fv-row mt-2'>
                <input
                  type='email'
                  className='form-control form-control-solid colored-border'
                  placeholder='Enter email Eg. demo@email.com'
                  value={botEmail}
                  onChange={handleBotEmailChange}
                />
              </div>
            </div>

            <button className='btn btn-success mt-2' type='button' onClick={handleApplySettings}>
              Apply Settings
            </button>
          </form>
        </div>

      </div>
    </div>
  )
}
